import { useState, useEffect, useCallback } from 'react';
import { ledgersAPI, storesAPI, transfersAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { 
  Store, CreditCard, IndianRupee, Search, RefreshCw, CheckCircle,
  FileText, History, ArrowLeft, Plus, Eye, Building2, AlertTriangle,
  Wallet, TrendingDown, Receipt
} from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'upi', label: 'UPI' },
  { value: 'rtgs', label: 'RTGS' },
  { value: 'neft', label: 'NEFT' }
];

const FranchisePayments = () => {
  const { toast } = useToast();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('record');
  
  // Ledgers
  const [ledgers, setLedgers] = useState([]);
  const [ledgersLoading, setLedgersLoading] = useState(false);
  
  // Payment history
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  
  // Pending invoices for selected franchise
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  
  // Payment form
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMode, setPaymentMode] = useState('bank_transfer');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [chequeNumber, setChequeNumber] = useState('');
  const [chequeDate, setChequeDate] = useState('');
  const [transactionRef, setTransactionRef] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch franchise ledgers
  const fetchLedgers = useCallback(async () => {
    setLedgersLoading(true);
    try {
      const response = await ledgersAPI.listFranchiseLedgers({ page_size: 100 });
      setLedgers(response.data?.items || []);
    } catch (error) {
      console.error('Error fetching ledgers:', error);
    } finally {
      setLedgersLoading(false);
    }
  }, []);

  // Fetch payment history
  const fetchPayments = useCallback(async () => {
    setPaymentsLoading(true);
    try {
      const response = await ledgersAPI.listFranchisePayments({ page_size: 100 });
      setPayments(response.data?.items || response.data || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setPaymentsLoading(false);
    }
  }, []);

  // Fetch pending invoices for franchise
  const fetchPendingInvoices = useCallback(async (storeId) => {
    if (!storeId) {
      setPendingInvoices([]);
      return;
    }
    
    setInvoicesLoading(true);
    try {
      // Fetch transfer orders with pending payment for this franchise
      const response = await transfersAPI.listOrders({ 
        destination_id: storeId, 
        status: 'completed',
        payment_pending: true,
        page_size: 50 
      });
      setPendingInvoices(response.data?.items || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setPendingInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLedgers();
    fetchPayments();
  }, [fetchLedgers, fetchPayments]);

  // Handle franchise selection
  const handleSelectFranchise = (franchiseId) => {
    const ledger = ledgers.find(l => l.store_id === franchiseId);
    setSelectedFranchise(ledger);
    setSelectedInvoices([]);
    setPaymentAmount('');
    fetchPendingInvoices(franchiseId);
  };

  // Toggle invoice selection
  const toggleInvoiceSelection = (invoice) => {
    const isSelected = selectedInvoices.some(i => i.id === invoice.id);
    if (isSelected) {
      setSelectedInvoices(prev => prev.filter(i => i.id !== invoice.id));
    } else {
      setSelectedInvoices(prev => [...prev, invoice]);
    }
  };

  // Calculate selected total
  const selectedTotal = selectedInvoices.reduce((sum, inv) => {
    const pending = (inv.grand_total || 0) - (inv.paid_amount || 0);
    return sum + pending;
  }, 0);

  // Auto-fill amount when invoices selected
  useEffect(() => {
    if (selectedInvoices.length > 0 && !paymentAmount) {
      setPaymentAmount(selectedTotal.toFixed(2));
    }
  }, [selectedInvoices, selectedTotal, paymentAmount]);

  // Reset form
  const resetForm = () => {
    setSelectedFranchise(null);
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentMode('bank_transfer');
    setPaymentAmount('');
    setSelectedInvoices([]);
    setBankName('');
    setAccountNumber('');
    setChequeNumber('');
    setChequeDate('');
    setTransactionRef('');
    setNotes('');
    setPendingInvoices([]);
  };

  // Submit payment
  const handleSubmitPayment = async () => {
    if (!selectedFranchise) {
      toast({ title: 'Error', description: 'Please select a franchise', variant: 'destructive' });
      return;
    }
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast({ title: 'Error', description: 'Please enter a valid amount', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const paymentData = {
        store_id: selectedFranchise.store_id,
        payment_date: paymentDate,
        payment_mode: paymentMode,
        amount: parseFloat(paymentAmount),
        mapped_invoices: selectedInvoices.map(inv => ({
          transfer_id: inv.id,
          transfer_number: inv.transfer_number,
          amount: (inv.grand_total || 0) - (inv.paid_amount || 0)
        })),
        bank_name: bankName || null,
        account_number: accountNumber || null,
        cheque_number: chequeNumber || null,
        cheque_date: chequeDate || null,
        transaction_reference: transactionRef || null,
        notes: notes || null
      };

      await ledgersAPI.createFranchisePayment(paymentData);
      toast({ title: 'Success', description: 'Payment recorded successfully' });
      
      resetForm();
      fetchLedgers();
      fetchPayments();
      setActiveTab('history');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to record payment',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Stats
  const totalReceivable = ledgers.reduce((sum, l) => sum + (l.current_balance || 0), 0);
  const totalCollected = ledgers.reduce((sum, l) => sum + (l.total_paid || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="franchise-payments-page">
      <Toaster />
      
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="p-4 lg:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-emerald-600" />
                Franchise Payments
              </h1>
              <p className="text-gray-500">Record and manage payments from franchise stores</p>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => { fetchLedgers(); fetchPayments(); }} variant="outline" className="gap-2">
                <RefreshCw className="w-4 h-4" /> Refresh
              </Button>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="px-4 lg:px-6">
          <TabsList className="bg-gray-100">
            <TabsTrigger value="record" className="data-[state=active]:bg-white gap-2">
              <Plus className="w-4 h-4" /> Record Payment
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-white gap-2">
              <History className="w-4 h-4" /> Payment History
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Stats Cards */}
      <div className="p-4 lg:p-6 pb-0">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Franchises</p>
                  <p className="text-2xl font-bold text-gray-800">{ledgers.length}</p>
                </div>
                <Store className="w-8 h-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Receivable</p>
                  <p className="text-2xl font-bold text-amber-600">
                    ₹{totalReceivable.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </p>
                </div>
                <TrendingDown className="w-8 h-8 text-amber-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Collected</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    ₹{totalCollected.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </p>
                </div>
                <Wallet className="w-8 h-8 text-emerald-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Recent Payments</p>
                  <p className="text-2xl font-bold text-blue-600">{payments.length}</p>
                </div>
                <Receipt className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="p-4 lg:p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Record Payment Tab */}
          <TabsContent value="record" className="mt-0 space-y-6">
            {/* Franchise Selection */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Store className="w-5 h-5 text-purple-600" />
                  Select Franchise
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Franchise Store *</Label>
                    <Select 
                      value={selectedFranchise?.store_id || ''} 
                      onValueChange={handleSelectFranchise}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Select franchise store..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white max-h-60">
                        {ledgers.map(ledger => (
                          <SelectItem key={ledger.store_id} value={ledger.store_id}>
                            <div className="flex items-center justify-between gap-4">
                              <span className="font-medium">{ledger.store_name}</span>
                              <span className="text-xs text-amber-600">
                                Balance: ₹{(ledger.current_balance || 0).toLocaleString('en-IN')}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Selected Franchise Info */}
                {selectedFranchise && (
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-start gap-3">
                      <Building2 className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-purple-600 text-xs">Store Name</p>
                          <p className="font-medium text-gray-800">{selectedFranchise.store_name}</p>
                        </div>
                        <div>
                          <p className="text-purple-600 text-xs">Store Code</p>
                          <p className="font-medium text-gray-800">{selectedFranchise.store_code}</p>
                        </div>
                        <div>
                          <p className="text-purple-600 text-xs">Current Balance</p>
                          <p className="font-bold text-amber-600">
                            ₹{(selectedFranchise.current_balance || 0).toLocaleString('en-IN')}
                          </p>
                        </div>
                        <div>
                          <p className="text-purple-600 text-xs">Credit Limit</p>
                          <p className="font-medium text-gray-800">
                            ₹{(selectedFranchise.credit_limit || 0).toLocaleString('en-IN')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pending Invoices */}
            {selectedFranchise && (
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-amber-600" />
                    Pending Invoices (Optional - Map Payment to Invoices)
                  </CardTitle>
                  <CardDescription>Select invoices to map this payment against</CardDescription>
                </CardHeader>
                <CardContent>
                  {invoicesLoading ? (
                    <div className="text-center py-8 text-gray-500">Loading invoices...</div>
                  ) : pendingInvoices.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                      <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                      <p>No pending invoices found</p>
                      <p className="text-sm">Payment can still be recorded as advance</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="w-12"></TableHead>
                            <TableHead>Transfer #</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Invoice Amount</TableHead>
                            <TableHead className="text-right">Paid</TableHead>
                            <TableHead className="text-right">Pending</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pendingInvoices.map(invoice => {
                            const pending = (invoice.grand_total || 0) - (invoice.paid_amount || 0);
                            const isSelected = selectedInvoices.some(i => i.id === invoice.id);
                            return (
                              <TableRow 
                                key={invoice.id} 
                                className={`cursor-pointer ${isSelected ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}
                                onClick={() => toggleInvoiceSelection(invoice)}
                              >
                                <TableCell>
                                  <Checkbox 
                                    checked={isSelected}
                                    onCheckedChange={() => toggleInvoiceSelection(invoice)}
                                  />
                                </TableCell>
                                <TableCell className="font-mono text-sm text-blue-600">
                                  {invoice.transfer_number}
                                </TableCell>
                                <TableCell className="text-gray-600">
                                  {formatDate(invoice.created_at)}
                                </TableCell>
                                <TableCell className="text-right font-medium">
                                  ₹{(invoice.grand_total || 0).toLocaleString('en-IN')}
                                </TableCell>
                                <TableCell className="text-right text-emerald-600">
                                  ₹{(invoice.paid_amount || 0).toLocaleString('en-IN')}
                                </TableCell>
                                <TableCell className="text-right font-bold text-amber-600">
                                  ₹{pending.toLocaleString('en-IN')}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  
                  {selectedInvoices.length > 0 && (
                    <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200 flex justify-between items-center">
                      <span className="text-emerald-700">
                        {selectedInvoices.length} invoice(s) selected
                      </span>
                      <span className="font-bold text-emerald-700">
                        Total: ₹{selectedTotal.toLocaleString('en-IN')}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Payment Details */}
            {selectedFranchise && (
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <IndianRupee className="w-5 h-5 text-emerald-600" />
                    Payment Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700">Payment Date *</Label>
                      <Input
                        type="date"
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                        className="bg-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Payment Mode *</Label>
                      <Select value={paymentMode} onValueChange={setPaymentMode}>
                        <SelectTrigger className="bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {PAYMENT_MODES.map(mode => (
                            <SelectItem key={mode.value} value={mode.value}>
                              {mode.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Amount (₹) *</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        placeholder="Enter amount"
                        className="bg-white text-lg font-bold"
                      />
                    </div>
                  </div>

                  {/* Mode-specific fields */}
                  {paymentMode === 'cheque' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="space-y-2">
                        <Label className="text-gray-700">Cheque Number</Label>
                        <Input
                          value={chequeNumber}
                          onChange={(e) => setChequeNumber(e.target.value)}
                          placeholder="Enter cheque number"
                          className="bg-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">Cheque Date</Label>
                        <Input
                          type="date"
                          value={chequeDate}
                          onChange={(e) => setChequeDate(e.target.value)}
                          className="bg-white"
                        />
                      </div>
                    </div>
                  )}

                  {['bank_transfer', 'upi', 'rtgs', 'neft'].includes(paymentMode) && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="space-y-2">
                        <Label className="text-gray-700">Bank Name</Label>
                        <Input
                          value={bankName}
                          onChange={(e) => setBankName(e.target.value)}
                          placeholder="Customer's bank"
                          className="bg-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">Account Number</Label>
                        <Input
                          value={accountNumber}
                          onChange={(e) => setAccountNumber(e.target.value)}
                          placeholder="Account number"
                          className="bg-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">Transaction Reference / UTR</Label>
                        <Input
                          value={transactionRef}
                          onChange={(e) => setTransactionRef(e.target.value)}
                          placeholder="UTR / Reference"
                          className="bg-white"
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-gray-700">Notes (Optional)</Label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add any notes..."
                      rows={2}
                      className="bg-white"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            {selectedFranchise && (
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={resetForm}
                  disabled={submitting}
                  className="gap-2"
                >
                  <ArrowLeft className="w-4 h-4" /> Clear Form
                </Button>
                <Button
                  onClick={handleSubmitPayment}
                  disabled={submitting || !paymentAmount}
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                >
                  <CheckCircle className="w-4 h-4" />
                  {submitting ? 'Processing...' : 'Record Payment'}
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Payment History Tab */}
          <TabsContent value="history" className="mt-0 space-y-4">
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Receipt #</TableHead>
                      <TableHead>Franchise</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Mode</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paymentsLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-10 text-gray-500">
                          Loading payments...
                        </TableCell>
                      </TableRow>
                    ) : payments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-10 text-gray-500">
                          <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p className="font-medium">No payments recorded yet</p>
                          <p className="text-sm">Payments will appear here</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      payments.map(payment => (
                        <TableRow key={payment.id} className="hover:bg-gray-50">
                          <TableCell>
                            <span className="font-mono text-sm text-emerald-600 font-medium">
                              {payment.receipt_number || payment.id?.slice(-8)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <p className="font-medium text-gray-800">{payment.store_name}</p>
                            <p className="text-xs text-gray-500">{payment.store_code}</p>
                          </TableCell>
                          <TableCell className="text-gray-600">
                            {formatDate(payment.payment_date)}
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-gray-100 text-gray-700 capitalize">
                              {payment.payment_mode?.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-bold text-emerald-600">
                            ₹{(payment.amount || 0).toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell className="text-gray-600 text-sm">
                            {payment.transaction_reference || payment.cheque_number || '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" className="text-gray-600">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default FranchisePayments;
